import { Utility, waitForMasonryInitialized, getVisibleMasonryColumns } from '../utility/utility_controller'
import { waitForInviewImagesLoaded } from '../animations/shared/waitForImageLoad'
import { getScopeForIsProjectOverlay, getScopeRoot } from '../scope/scope'

/**
 * Build Animation group resolver.
 *
 * On the editor side, "Build Animation" creates a GROUP referenced by
 * each member element via `data-lay-build-anim-group="GROUP_ID"`. The
 * group's settings (stagger, duration, easing, order, scale, …) are
 * encoded into the `.grid` ancestor's `data-lay-build-anim-groups` JSON
 * attribute.
 *
 * This module reads those attributes, sorts each group's members by the
 * group's `order`, computes per-member delay + duration, and writes the
 * result onto the member elements as the same `data-lay-scroll-anim-*`
 * / `data-duration` / `data-delay` attributes that the scroll-animation
 * driver and the text-animation driver already read. That way both
 * drivers stay group-agnostic — they consume the attributes as if PHP
 * had emitted them directly, which keeps the driver hot path simple.
 *
 * Run this BEFORE `scroll_animations_controller.i()` and any text
 * animation parser. It mutates the DOM (sets attributes) so subsequent
 * reads pick up the resolved values.
 */

// Group settings store `staggerMs` and `durationMs` in milliseconds —
// the user-facing unit everywhere in lay-theme. The scroll-animation
// engine expects frame counts in its data attrs, so we convert
// `ms / FRAME_MS` when writing scroll-anim attrs. The text-animation
// engine takes ms directly (anime.js convention), so those attrs go
// through unchanged.
const FRAME_MS = 1000 / 60

const easings = {
	linear: (t) => t,
	easeInQuad: (t) => t * t,
	easeOutQuad: (t) => t * (2 - t),
	easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
	easeInCubic: (t) => t * t * t,
	easeOutCubic: (t) => --t * t * t + 1,
	easeInOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
	easeInQuart: (t) => t * t * t * t,
	easeOutQuart: (t) => 1 - --t * t * t * t,
	easeInOutQuart: (t) => (t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t),
	easeOutQuint: (t) => 1 + --t * t * t * t * t,
	easeInSine: (t) => 1 - Math.cos((t * Math.PI) / 2),
	easeOutSine: (t) => Math.sin((t * Math.PI) / 2),
	easeInOutSine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
	easeInExpo: (t) => (t === 0 ? 0 : Math.pow(2, 10 * (t - 1))),
	easeOutExpo: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
	easeInOutExpo: (t) => {
		if (t === 0) return 0
		if (t === 1) return 1
		return t < 0.5 ? Math.pow(2, 20 * t - 10) / 2 : (2 - Math.pow(2, -20 * t + 10)) / 2
	},
	easeOutCirc: (t) => Math.sqrt(1 - --t * t),
	easeInOutCirc: (t) => {
		return t < 0.5
			? (1 - Math.sqrt(1 - Math.pow(2 * t, 2))) / 2
			: (Math.sqrt(1 - Math.pow(-2 * t + 2, 2)) + 1) / 2
	},
	easeOutBack: (t) => {
		const c1 = 1.70158
		const c3 = c1 + 1
		const raw = 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
		return Math.max(0, Math.min(1, raw))
	},
	easeInOutBack: (t) => {
		const c1 = 1.70158
		const c2 = c1 * 1.525
		const raw = t < 0.5
			? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
			: (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2
		return Math.max(0, Math.min(1, raw))
	},
}
const sampleEasing = (name, t) => {
	const fn = easings[name] || easings.easeOutSine
	return fn(Math.max(0, Math.min(1, t)))
}

// Mirror of the editor's `computeTimings` — kept in lockstep with
// gridder/tsx/util/buildSequentialAnimation.ts so the preview the user
// designs and the runtime they see are identical.
function computeTimings(n, staggerMs, durationMs, staggerEasing, scalePerElementDuration) {
	if (n === 0) return []
	if (n === 1) return [{ delay: 0, duration: Math.max(1, Math.round(durationMs)) }]
	const baseDuration = Math.max(1, Math.round(durationMs))
	const staggerBudget = Math.max(0, staggerMs) * (n - 1)
	const out = []
	for (let i = 0; i < n; i++) {
		const tLinear = i / (n - 1)
		const tEased = sampleEasing(staggerEasing, tLinear)
		let duration = baseDuration
		if (scalePerElementDuration) {
			// Late starters animate longer so "start order = finish order"
			// holds regardless of easing / sort direction. See the editor
			// mirror in `buildSequentialAnimation.ts` for the rationale.
			const scale = 0.6 + tEased * 0.8
			duration = Math.max(1, Math.round(baseDuration * scale))
		}
		const delay = Math.round(tEased * staggerBudget)
		out.push({ delay, duration })
	}
	return out
}

// Sort positions come from the editor grid model. Walks up to the
// nearest `.col` and reads `colstart-N` / `colend-N` + the parent
// `row-N` class. Matches the data the user sees in the gridder
// exactly. Rendered-alignment shifts (offset / align / push) are
// ignored — the row/col data is what looked smoothest in testing;
// rect-based and row-band variants produced jittery cascades and
// were dropped.
function readColPosition(el) {
	const col = el.closest && el.closest('.col')
	if (!col) return { row: 0, col: 0, colspan: 1, right: 1, bottom: 0 }
	let row = 0
	let colIdx = 0
	let colspan = 1
	const cls = (col.className || '').toString().split(/\s+/)
	for (const c of cls) {
		if (c.indexOf('colstart-') === 0) colIdx = parseInt(c.slice('colstart-'.length), 10) || 0
		else if (c.indexOf('colend-') === 0) {
			const end = parseInt(c.slice('colend-'.length), 10)
			if (!isNaN(end)) colspan = Math.max(1, end - colIdx)
		}
	}
	let n = col.parentElement
	while (n) {
		const ncls = (n.className || '').toString().split(/\s+/)
		for (const c of ncls) {
			if (c.indexOf('row-') === 0 && c.length > 'row-'.length) {
				const r = parseInt(c.slice('row-'.length), 10)
				if (!isNaN(r)) {
					row = r
					n = null
					break
				}
			}
		}
		if (n) n = n.parentElement
	}
	return {
		// Compose a rect-shaped object so `sortByOrder` below stays a
		// single switch — `top`/`bottom` are the row index (no rowspan
		// in lay-theme, so top === bottom), `left`/`right` are the col
		// edges.
		top: row,
		bottom: row,
		left: colIdx,
		right: colIdx + colspan,
	}
}

function sortByOrder(members, order) {
	const sorted = members.slice()
	switch (order) {
		case 'topLeftToBottomRight':
		case 'topToBottom':
			// Same-row tiebreaker uses left edge so within a row reads L→R.
			sorted.sort((a, b) => (a.pos.top - b.pos.top) || (a.pos.left - b.pos.left))
			break
		case 'leftToRight':
			sorted.sort((a, b) => (a.pos.left - b.pos.left) || (a.pos.top - b.pos.top))
			break
		case 'rightToLeft':
			// Rightmost `right` edge (col + colspan in the grid model)
			// first — wider elements that reach further right come
			// before narrower ones starting from a higher col index.
			sorted.sort((a, b) => (b.pos.right - a.pos.right) || (b.pos.bottom - a.pos.bottom))
			break
	}
	return sorted
}

function readGroupsFromGrid(gridEl) {
	const raw = gridEl.getAttribute('data-lay-build-anim-groups')
	if (!raw) return null
	try {
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return null
		return parsed
	} catch (e) {
		console.warn('[lay-anim] failed to parse data-lay-build-anim-groups JSON', e)
		return null
	}
}

function shouldSkipBuildAnimationGroupsForGeneratedPhoneLayout(grid) {
	// Automatically generated phone layouts use the desktop `#grid`
	// markup and stack it into a simple phone flow. A Build Animation
	// Group is a carefully timed desktop/custom-layout choreography, so
	// we don't resolve group timings for that generated phone view. The
	// custom phone grid has its own markup and still resolves normally.
	return !!(
		grid &&
		grid.id === 'grid' &&
		Utility &&
		Utility.isPhoneSize &&
		grid.closest &&
		grid.closest('.lay-content.nocustomphonegrid')
	)
}

/**
 * Resolve every Build Animation group on the page: read settings from
 * the grid's data attribute, gather members via their groupId, sort
 * them, compute timings, write `data-duration` / `data-delay` (text
 * elements) or `data-lay-scroll-anim-duration-frames` /
 * `data-lay-scroll-anim-delay-frames` (scroll-anim elements) onto each
 * member. Idempotent — safe to call multiple times (e.g. after
 * `newpageshown` or AJAX navs).
 */
function resolveBuildAnimationGroups(scopeRoot) {
	const root = scopeRoot || document
	const grids = root.querySelectorAll('.grid[data-lay-build-anim-groups]')
	for (let g = 0; g < grids.length; g++) {
		const grid = grids[g]
		if (shouldSkipBuildAnimationGroupsForGeneratedPhoneLayout(grid)) {
			continue
		}
		const groups = readGroupsFromGrid(grid)
		if (!groups) continue

		// Per-grid: collect members of each group + their grid position.
		const allMembers = grid.querySelectorAll('[data-lay-build-anim-group]')
		const byGroup = {}
		for (let i = 0; i < allMembers.length; i++) {
			const el = allMembers[i]
			const gid = el.getAttribute('data-lay-build-anim-group')
			if (!gid || !groups[gid]) continue
			if (!byGroup[gid]) byGroup[gid] = []
			byGroup[gid].push({ el, pos: readColPosition(el) })
		}

		// Per-group: sort + compute + write attributes.
		for (const gid of Object.keys(byGroup)) {
			const settings = groups[gid]
			const members = sortByOrder(byGroup[gid], settings.order)
			const hasScrollMembers = members.some((member) => {
				return member && member.el && member.el.hasAttribute('data-lay-scroll-anim')
			})
			const timings = computeTimings(
				members.length,
				settings.staggerMs,
				settings.durationMs,
				settings.staggerEasing,
				settings.scalePerElementDuration,
			)
			// Group-level start offset (ms) — added to every member's
			// resolved delay. Mirrors `resolveGroupTimings` in the editor.
			const groupDelay = (typeof settings.delayMs === 'number' && isFinite(settings.delayMs) && settings.delayMs > 0)
				? settings.delayMs
				: 0
			for (let i = 0; i < members.length; i++) {
				const el = members[i].el
				const rawT = timings[i]
				const t = { delay: rawT.delay + groupDelay, duration: rawT.duration }
				// Scroll-anim wrap: emits via `data-lay-scroll-anim-*`.
				// The scroll-anim engine expects frame counts in its
				// `-frames` attrs, so we convert ms → frames here.
				// `easing` (`elementEasing`) and `trigger-top`
				// (`triggerTop`) are also written by runtime.
				if (el.hasAttribute('data-lay-scroll-anim')) {
					el.setAttribute('data-lay-scroll-anim-duration-frames', String(Math.round(t.duration / FRAME_MS)))
					el.setAttribute('data-lay-scroll-anim-delay-frames', String(Math.round(t.delay / FRAME_MS)))
					// Group-level "Scale Animations based on element size":
					// the scroll-anim engine caps the translate distance at
					// the element's own width/height when this attr is set,
					// so a small thumbnail moves only its own span while a
					// hero section gets the full preset distance.
					if (settings.scaleAnimationsBasedOnSize !== false) {
						el.setAttribute('data-lay-scroll-anim-scale-to-elsize', '1')
					} else if (el.hasAttribute('data-lay-scroll-anim-scale-to-elsize')) {
						el.removeAttribute('data-lay-scroll-anim-scale-to-elsize')
					}
					if (settings.elementEasing) {
						el.setAttribute('data-lay-scroll-anim-easing', String(settings.elementEasing))
					}
					if (settings.triggerTop != null && isFinite(settings.triggerTop)) {
						el.setAttribute('data-lay-scroll-anim-trigger-top', String(settings.triggerTop))
					}
					// Group-level Disable-on-Phone propagates to each
					// member via the existing per-element attribute, which
					// the scroll-animation driver already honours by
					// bypassing the rAF loop at phone screen size.
					if (settings.disableOnPhone) {
						el.setAttribute('data-lay-scroll-anim-disable-on-phone', '1')
					}
					// Group-level "Animate again on re-entry" OVERRIDES
					// each member's per-element setting. The group is the
					// single source of truth for the cascade; without
					// this overwrite, a member that had been edited
					// with replay=false before joining the group would
					// silently keep the old behaviour. Same convention
					// as `disable-on-phone` / `easing` above: write only
					// when explicitly opted out so the default replay
					// matches the legacy behaviour.
					if (settings.repeatOnReEnter === false) {
						el.setAttribute('data-lay-scroll-anim-repeat-on-re-enter', '0')
						el.removeAttribute('data-lay-scroll-anim-repeat-on-re-enter-direction')
					} else {
						const reEntryDirection = settings.repeatOnReEnterDirection === 'from_above' || settings.repeatOnReEnterDirection === 'both' ? settings.repeatOnReEnterDirection : 'from_below'
						el.removeAttribute('data-lay-scroll-anim-repeat-on-re-enter')
						el.setAttribute('data-lay-scroll-anim-repeat-on-re-enter-direction', reEntryDirection)
					}
				}
				// Text animation: group owns `delay`, `easing`, `top`,
				// plus the new `data-build-anim-total-duration` — the
				// slot's target total ms that textanimations.js scales
				// the element's per-unit `duration` and `stagger` to fit.
				// PHP always emits `data-duration` (per-unit) and
				// `data-stagger` if set; we DON'T overwrite them — they
				// represent the user's pre-group design at 1× speed.
				// At runtime, textanimations.js reads
				// `data-build-anim-total-duration` and scales per-unit
				// values proportionally to the live sub-item count
				// (words / chars / lines after splitting), so a
				// 10-word vs 100-char text in the same slot both finish
				// in the same total time.
				if (el.classList.contains('lay-text-animation') || el.classList.contains('lay-build-anim-text-disabled')) {
					el.setAttribute('data-build-anim-total-duration', String(Math.round(t.duration)))
					el.setAttribute('data-delay', String(Math.round(t.delay)))
					if (hasScrollMembers) {
						el.setAttribute('data-lay-build-anim-wait-for-scroll-group', '1')
					} else {
						el.removeAttribute('data-lay-build-anim-wait-for-scroll-group')
					}
					if (settings.textEasing) {
						el.setAttribute('data-animation-easing', String(settings.textEasing))
					}
					if (settings.triggerTop != null && isFinite(settings.triggerTop)) {
						el.setAttribute('data-top', String(settings.triggerTop))
					}
					// Group-level "Animate again on re-entry" override —
					// same pattern as the scroll-anim branch above. The
					// group is the single source of truth, so we
					// overwrite (or clear) the per-element attr.
					if (settings.repeatOnReEnter === false) {
						el.setAttribute('data-repeat-on-re-enter', '0')
						el.removeAttribute('data-repeat-on-re-enter-direction')
					} else {
						const reEntryDirection = settings.repeatOnReEnterDirection === 'from_above' || settings.repeatOnReEnterDirection === 'both' ? settings.repeatOnReEnterDirection : 'from_below'
						el.removeAttribute('data-repeat-on-re-enter')
						el.setAttribute('data-repeat-on-re-enter-direction', reEntryDirection)
					}
					// Group-level Disable-on-Phone: the text-animation
					// driver doesn't have its own disable-on-phone attr,
					// so we strip the `lay-text-animation` class entirely
					// at phone size — without that class, the splitter
					// + animator never pick it up. Add a marker class so
					// future runs (e.g. after a window resize) can find
					// + restore the element if the user grows back to
					// desktop size.
					const isPhone = !!(Utility && Utility.isPhoneSize)
					if (settings.disableOnPhone && isPhone) {
						el.classList.remove('lay-text-animation')
						el.classList.add('lay-build-anim-text-disabled')
					} else if (el.classList.contains('lay-build-anim-text-disabled')) {
						el.classList.add('lay-text-animation')
						el.classList.remove('lay-build-anim-text-disabled')
					}
				}
			}
		}
	}
}

/**
 * Sets `window._layAnimTimeOrigin` once the page is "really ready" for
 * Build Animation Group reveals — images decoded, masonry laid out.
 * Until this fires, the scroll-anim engine's pending-phase delay countdown
 * is held at the trigger moment (see `Math.max(_timedStartMs, origin)` in
 * scroll_animations.js).
 *
 * Why this exists separately from the nav-anim path: nav-anim only
 * emits `transition_in` when the Lay Options checkbox
 * "Use revealing transition on first visit" is on. That checkbox is OFF
 * by default, so on most sites Build Animation Group elements would
 * otherwise trigger before images / masonry settle and start their delay
 * countdown from an unstable layout. Setting the origin here guarantees a
 * sensible reference point regardless of nav-anim configuration.
 *
 * Idempotent: nav-anim's myTimeline.begin (when active) sets the origin
 * earlier, in which case this is a no-op. Whoever sets it first wins.
 */
function getColumnsForReadyWait(scopeRoot) {
	const jq = window.jQuery
	if (typeof jq !== 'function') return null

	const columnSelector = '.col:not(.type-stack), .col.type-stack .col'
	const root = scopeRoot && scopeRoot.nodeType ? scopeRoot : document
	if (root === document) {
		return jq('body>.lay-content .col:not(.type-stack), body>.lay-content .col.type-stack .col, #lay-project-overlay>.lay-content .col:not(.type-stack), #lay-project-overlay>.lay-content .col.type-stack .col')
	}
	return jq(root).find(columnSelector).add(jq(root).filter(columnSelector))
}

function setupTimeOriginAfterReady(scopeRoot) {
	// Always update — see the matching note in
	// navigation_animations_controller.js's myTimeline.begin. SPA navs
	// re-arm this via the `newpageshown` listener below; the new page's
	// build-anim members must sync with that page's reveal, not a stale
	// origin from the first load.
	const originGeneration = (window._layBuildAnimOriginGeneration || 0) + 1
	window._layBuildAnimOriginGeneration = originGeneration
	window._layBuildAnimOriginPending = true
	const markReady = () => {
		if (window._layBuildAnimOriginGeneration !== originGeneration) {
			return
		}
		window._layAnimTimeOrigin = performance.now()
		window._layBuildAnimOriginPending = false
		try {
			window.dispatchEvent(new CustomEvent('lay-build-anim-origin-ready'))
		} catch (_) {
			// CustomEvent constructor unsupported in legacy browsers.
		}
	}
	// Image-load shortcut mirrors navigation_animations_controller's:
	// loadingBlur=on+lazyLoading=on → tiny placeholders, instant.
	// loadingBlur=blur → blurred placeholder already visible, no need to
	// wait for the full image. Skipping the wait lets the cascade start
	// promptly instead of sitting idle while images decode.
	const skipImageWait = (
		(layData.loadingBlur == 'on' && layData.lazyLoading == 'on') ||
		layData.loadingBlur == 'blur'
	)
	const proceedAfterImages = () => {
		// Masonry-init gate: same pass nav-anim does in transition_in. If
		// any masonry column is on the page, wait for it to finish placing
		// items before stamping the origin — otherwise build-anim members
		// inside masonry would start their delay relative to a pre-layout
		// position.
		const jq = window.jQuery
		if (typeof jq !== 'function') {
			markReady()
			return
		}
		const $columns = getColumnsForReadyWait(scopeRoot)
		const masonryCols = getVisibleMasonryColumns($columns)
		if (masonryCols && masonryCols.length > 0) {
			jq.when(waitForMasonryInitialized(masonryCols))
				.then(() => markReady())
				.catch(() => markReady())
			return
		}
		markReady()
	}
	if (skipImageWait) {
		proceedAfterImages()
		return
	}
	const jq = window.jQuery
	if (typeof jq !== 'function') {
		// jQuery isn't loaded yet — fall back to firing on next tick.
		// The animations will start slightly early but won't fail.
		setTimeout(markReady, 0)
		return
	}
	const $columns = getColumnsForReadyWait(scopeRoot)
	waitForInviewImagesLoaded($columns, proceedAfterImages)
	// Safety: if waitForInviewImagesLoaded never resolves (404, network
	// hang), the animations would stay frozen forever. Fall through to
	// `markReady` after 3s so the page becomes functional regardless.
	setTimeout(markReady, 3000)
}

const build_animation_groups_controller = {
	resolve: resolveBuildAnimationGroups,
	i: function () {
		// Initial run at module init.
		resolveBuildAnimationGroups(document)
		setupTimeOriginAfterReady(document)
		if (window.laytheme && typeof window.laytheme.on === 'function') {
			// Re-run on every AJAX nav so newly-rendered grids pick up
			// their group timings before scroll_animations re-collects.
			window.laytheme.on('newpageshown', (layoutObj, type, obj, isProjectOverlayObj) => {
				const scopeRoot = getScopeRoot(getScopeForIsProjectOverlay(isProjectOverlayObj))
				resolveBuildAnimationGroups(scopeRoot)
				// Re-arm the readiness gate for SPA navs. Without this,
				// `_layAnimTimeOrigin` stays at the first page's value,
				// and a member with `delayMs=2000` on page 2 starts its
				// countdown relative to page 1's images-loaded moment —
				// usually far in the past, so the delay effectively
				// shrinks to zero. Re-running stamps a fresh origin
				// after the new page's images + masonry settle.
				setupTimeOriginAfterReady(scopeRoot)
			})
			window.laytheme.on('page_overlay_display_block', ($overlay) => {
				const scopeRoot = ($overlay && $overlay.jquery && $overlay[0]) || document
				resolveBuildAnimationGroups(scopeRoot)
				setupTimeOriginAfterReady(scopeRoot)
			})
			window.laytheme.on('page_overlay_open_end', ($overlay) => {
				const scopeRoot = ($overlay && $overlay.jquery && $overlay[0]) || document
				resolveBuildAnimationGroups(scopeRoot)
				setupTimeOriginAfterReady(scopeRoot)
			})
			// Re-run on viewport resize so the `disableOnPhone` gate
			// can toggle (strip / restore `.lay-text-animation` class
			// when the user crosses the phone breakpoint).
			window.laytheme.on('window_width_changed', () => resolveBuildAnimationGroups(document))
		}
	},
}

export default build_animation_groups_controller
